import { useEffect, useRef, useState } from 'react';
import { Download, Image as ImageIcon, ImagePlus, Type as TypeIcon } from 'lucide-react';
import type { CommandExecution, HistoryManager } from '../core/commands';
import { findLayer, type EditorDocument, type RasterLayer, type TextLayer } from '../core/model';
import { FillTextCommand, layerSlot, listSlots, ReplaceRasterCommand, SetLayerSlotCommand, slotRoleForLayer, type SlotInfo } from '../core/template';
import { RasterTileStore } from '../core/tiles';
import { measureTextWidth } from '../tooling/text-measure';
import { modeLabel, type MediaMode } from '../core/media-mode';
import type { DocumentSession } from '../core/session';
import { fillTextSlot, markSlot, renameSlot, runFillOperation, setSlotPrompt, unmarkSlot } from './fill-intents';
import type { PanelOperation } from './layer-intents';

interface FillPanelProps {
  document: EditorDocument;
  history: HistoryManager;
  mediaMode: MediaMode;
  onExport: (format: 'png' | 'jpeg') => void;
  onExportAll?: (() => void) | undefined;
  sessionCount?: number;
  /**
   * The document's session. When given, slot edits dispatch as user intents
   * (the same definitions agents use; byte parity in fill-intents.test.ts);
   * without it they execute their native commands directly.
   */
  session?: DocumentSession | undefined;
}

/** Decodes an image file and center-crops it to cover the target size. */
async function coverImageData(file: File, width: number, height: number): Promise<ImageData> {
  const bitmap = await createImageBitmap(file);
  try {
    const canvas = globalThis.document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas 2D is unavailable.');
    const scale = Math.max(width / bitmap.width, height / bitmap.height);
    const drawWidth = bitmap.width * scale;
    const drawHeight = bitmap.height * scale;
    context.drawImage(bitmap, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
    return context.getImageData(0, 0, width, height);
  } finally {
    bitmap.close();
  }
}

function SlotThumb({ store }: { store: RasterTileStore }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const revision = store.storeRevision();
  useEffect(() => {
    const canvas = ref.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    const image = store.toImageData();
    const source = globalThis.document.createElement('canvas');
    source.width = image.width;
    source.height = image.height;
    const sourceContext = source.getContext('2d');
    if (!sourceContext) return;
    sourceContext.putImageData(image, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    const scale = Math.min(canvas.width / image.width, canvas.height / image.height);
    const width = image.width * scale;
    const height = image.height * scale;
    context.drawImage(source, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height);
  }, [store, revision]);
  return <div className="fill-thumb"><canvas ref={ref} width={72} height={54} /></div>;
}

export function FillPanel({ document, history, mediaMode, onExport, onExportAll, sessionCount = 1, session }: FillPanelProps) {
  const [textDrafts, setTextDrafts] = useState<Record<string, string>>({});
  const [nameDrafts, setNameDrafts] = useState<Record<string, string>>({});
  const [promptDrafts, setPromptDrafts] = useState<Record<string, string>>({});
  const [slotName, setSlotName] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const pendingImageLayer = useRef<{ layerId: string; documentId: string } | null>(null);

  useEffect(() => {
    setTextDrafts({});
    setNameDrafts({});
    setPromptDrafts({});
    setNotice(null);
    pendingImageLayer.current = null;
  }, [document.id]);

  const allEntries = listSlots(document);
  const entries = allEntries.filter(({ slot }) => mediaMode === 'all' || (mediaMode === 'vector' ? slot.role === 'text' : slot.role === 'image'));
  const hiddenSlots = allEntries.length - entries.length;
  const active = document.activeLayerIds[0] ? findLayer(document.root, document.activeLayerIds[0]) : undefined;
  const candidateRole = active ? slotRoleForLayer(active) : null;
  const activeRole = candidateRole && (mediaMode === 'all' || (mediaMode === 'vector' ? candidateRole === 'text' : candidateRole === 'image')) ? candidateRole : null;
  const activeSlot = active ? layerSlot(active) : null;

  const report = (result: CommandExecution): boolean => {
    setNotice(result.outcome === 'rejected' ? result.reason : null);
    return result.outcome === 'changed';
  };
  /** The intent path when the session is known, else the panel's original direct command. */
  const run = (operation: PanelOperation, direct: () => CommandExecution): CommandExecution => (session && session.document === document ? runFillOperation(session, operation) : direct());

  const clearDraft = (setter: typeof setTextDrafts, id: string) => {
    setter((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
  };

  const commitText = (layer: TextLayer, value: string) => {
    clearDraft(setTextDrafts, layer.id);
    if (value === layer.text.value) return;
    // Longer fills must not clip in the template's fixed box: grow it and keep
    // right/center-anchored layers visually anchored — one undoable step.
    const style = layer.text.style;
    const measured = Math.ceil(measureTextWidth(value, style.family, style.weight, style.size, style.tracking) * 1.05 + 4);
    const growth = Math.max(0, measured - layer.text.box.width);
    const shift = growth > 0 && style.align !== 'left' ? (style.align === 'right' ? growth : growth / 2) : 0;
    const direct = () => history.execute(document, new FillTextCommand(layer.id, value, growth, shift));
    // slot.fill addresses slots by name: only a uniquely named slot is this layer (imported templates may repeat names).
    const slot = layerSlot(layer);
    const unique = slot !== null && listSlots(document).filter((entry) => entry.slot.name === slot.name).length === 1;
    report(slot && unique ? run(fillTextSlot(slot.name, value), direct) : direct());
  };

  const commitRename = (layerId: string, slot: SlotInfo, raw: string) => {
    clearDraft(setNameDrafts, layerId);
    const name = raw.trim();
    if (!name || name === slot.name) return;
    report(run(renameSlot(layerId, name), () => history.execute(document, new SetLayerSlotCommand(layerId, { ...slot, name }))));
  };

  const commitPrompt = (layerId: string, slot: SlotInfo, raw: string) => {
    clearDraft(setPromptDrafts, layerId);
    const prompt = raw.trim();
    if (prompt === (slot.prompt ?? '')) return;
    const next: SlotInfo = { name: slot.name, role: slot.role };
    if (prompt) next.prompt = prompt;
    report(run(setSlotPrompt(layerId, slot, prompt), () => history.execute(document, new SetLayerSlotCommand(layerId, next))));
  };

  const replaceImage = (layer: RasterLayer, slotLabel: string, file: File) => {
    void coverImageData(file, layer.raster.width, layer.raster.height)
      .then((image) => {
        report(history.execute(document, new ReplaceRasterCommand(layer.id, RasterTileStore.fromImageData(image), `Fill “${slotLabel}”`)));
      })
      .catch((reason: unknown) => setNotice(reason instanceof Error ? reason.message : 'Could not read the image.'));
  };

  const markActive = () => {
    if (!active || !activeRole) return;
    const name = slotName.trim() || active.name.trim();
    if (!name) return;
    if (report(run(markSlot(active.id, name, activeRole), () => history.execute(document, new SetLayerSlotCommand(active.id, { name, role: activeRole }))))) setSlotName('');
  };

  return (
    <div className="fill-panel">
      <div className="fill-mark">
        <div className="fill-section-title">Template slots</div>
        <div className="fill-doc-line">{document.name} · {entries.length} visible slot{entries.length === 1 ? '' : 's'}{hiddenSlots ? ` · ${hiddenSlots} hidden by ${modeLabel(mediaMode)}` : ''}</div>
        {active && activeRole ? (
          activeSlot ? (
            <div className="fill-mark-row">
              <span>“{active.name}” is slot <strong>{activeSlot.name}</strong></span>
              <button type="button" onClick={() => report(run(unmarkSlot(active.id), () => history.execute(document, new SetLayerSlotCommand(active.id, null))))}>Unmark</button>
            </div>
          ) : (
            <div className="fill-mark-row">
              <input
                value={slotName}
                placeholder={`Slot name for “${active.name}”`}
                aria-label="Slot name"
                onChange={(event) => setSlotName(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') markActive(); }}
              />
              <button type="button" onClick={markActive}>Mark {activeRole}</button>
            </div>
          )
        ) : (
          <p className="fill-hint">Select a {mediaMode === 'vector' ? 'text' : mediaMode === 'raster' ? 'raster' : 'text or raster'} layer to mark it as a reusable slot.</p>
        )}
      </div>
      <div className="fill-list">
        {entries.length === 0 ? <p className="fill-hint">{hiddenSlots ? `Every marked slot is hidden by the ${modeLabel(mediaMode)} workspace.` : 'No slots yet. Mark the layers that change between events — text slots for titles, dates, and lineups; image slots for artwork. Locked base layers stay untouched.'}</p> : null}
        {entries.map(({ layer, slot }) => (
          <div className="fill-slot" key={layer.id}>
            <div className="fill-slot-head">
              {slot.role === 'text' ? <TypeIcon size={12} /> : <ImageIcon size={12} />}
              <input
                className="fill-slot-name"
                aria-label={`Rename slot ${slot.name}`}
                value={nameDrafts[layer.id] ?? slot.name}
                onChange={(event) => setNameDrafts((current) => ({ ...current, [layer.id]: event.target.value }))}
                onBlur={(event) => commitRename(layer.id, slot, event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }}
              />
              <span>{layer.name}</span>
            </div>
            {slot.role === 'text' && layer.kind === 'text' ? (
              <textarea
                rows={2}
                aria-label={`Slot ${slot.name} text`}
                value={textDrafts[layer.id] ?? layer.text.value}
                onChange={(event) => setTextDrafts((current) => ({ ...current, [layer.id]: event.target.value }))}
                onBlur={(event) => commitText(layer, event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    event.currentTarget.blur();
                  }
                }}
              />
            ) : null}
            {slot.role === 'image' && layer.kind === 'raster' ? (
              <div className="fill-image-body">
                <SlotThumb store={layer.raster} />
                <div className="fill-image-controls">
                  <button
                    type="button"
                    className="fill-replace"
                    onClick={() => {
                      pendingImageLayer.current = { layerId: layer.id, documentId: document.id };
                      fileInput.current?.click();
                    }}
                  >
                    <ImagePlus size={13} /> Replace · {layer.raster.width}×{layer.raster.height}
                  </button>
                  <input
                    className="fill-prompt"
                    aria-label={`Slot ${slot.name} generation prompt`}
                    placeholder="Generation prompt (saved in template)"
                    value={promptDrafts[layer.id] ?? slot.prompt ?? ''}
                    onChange={(event) => setPromptDrafts((current) => ({ ...current, [layer.id]: event.target.value }))}
                    onBlur={(event) => commitPrompt(layer.id, slot, event.target.value)}
                    onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        ))}
      </div>
      {notice ? <p className="fill-notice">{notice}</p> : null}
      {mediaMode !== 'vector' ? <div className="fill-actions">
        <button type="button" onClick={() => onExport('png')}><Download size={13} /> Export PNG</button>
        {onExportAll ? <button type="button" onClick={onExportAll}><Download size={13} /> Export all ({sessionCount})</button> : null}
      </div> : null}
      <input
        ref={fileInput}
        type="file"
        hidden
        accept="image/png,image/jpeg,image/webp"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          const pending = pendingImageLayer.current;
          pendingImageLayer.current = null;
          if (!file || !pending) return;
          if (pending.documentId !== document.id) {
            setNotice('The document changed while choosing a file; the image was not applied.');
            return;
          }
          const layer = findLayer(document.root, pending.layerId);
          if (layer?.kind !== 'raster') return;
          replaceImage(layer, layerSlot(layer)?.name ?? layer.name, file);
        }}
      />
    </div>
  );
}
